import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GitPullRequest, Send, CheckCircle2, AlertCircle, Loader2, Paperclip, X, FileImage, FileText, File } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PUBLIC_API = process.env.REACT_APP_BACKEND_URL + '/api/public/cr';

const PublicCRForm = () => {
  const { token } = useParams();
  const [projectInfo, setProjectInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    reason: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    requester_department: ''
  });

  useEffect(() => {
    fetchProjectInfo();
  }, [token]);

  const fetchProjectInfo = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await axios.get(`${PUBLIC_API}/form/${token}`);
      setProjectInfo(response.data);
    } catch (err) {
      setError('Dieser Formular-Link ist ungültig oder wurde deaktiviert.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title || !formData.description || !formData.reason || 
        !formData.requester_name || !formData.requester_email) {
      setError('Bitte füllen Sie alle Pflichtfelder aus.');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.requester_email)) {
      setError('Bitte geben Sie eine gültige E-Mail-Adresse ein.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // First submit the CR
      const response = await axios.post(`${PUBLIC_API}/submit`, {
        project_token: token,
        ...formData
      });
      
      // If there are attachments, upload them
      if (attachments.length > 0 && response.data.id) {
        for (const file of attachments) {
          const uploadData = new FormData();
          uploadData.append('file', file);
          try {
            await axios.post(`${PUBLIC_API}/${response.data.id}/attachments?token=${token}`, uploadData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (uploadErr) {
            console.error('Error uploading attachment:', uploadErr);
          }
        }
      }
      
      setSuccess(response.data);
      // Reset form
      setAttachments([]);
      setFormData({
        title: '',
        description: '',
        reason: '',
        priority: 'medium',
        requester_name: '',
        requester_email: '',
        requester_department: ''
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="py-12 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
            <p className="mt-4 text-muted-foreground">Formular wird geladen...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state - invalid token
  if (error && !projectInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h2 className="text-xl font-semibold mb-2">Formular nicht verfügbar</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-lg">
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h2 className="text-2xl font-bold mb-2">Vielen Dank!</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Ihr Change Request wurde erfolgreich eingereicht.
            </p>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <strong>Referenznummer:</strong> {success.cr_number}
              </p>
              <p className="text-xs text-green-600 mt-1">
                Bitte notieren Sie sich diese Nummer für Rückfragen.
              </p>
            </div>
            <Button onClick={() => setSuccess(null)} variant="outline">
              Weiteren Change Request einreichen
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <GitPullRequest className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Change Request einreichen</h1>
          <p className="text-muted-foreground mt-2">
            Projekt: <span className="font-semibold text-gray-700">{projectInfo?.project_name}</span>
          </p>
        </div>

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Änderungsantrag</CardTitle>
            <CardDescription>
              Beschreiben Sie die gewünschte Änderung. Der Projektleiter wird Ihren Antrag prüfen.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error message */}
              {error && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {error}
                </div>
              )}

              {/* Requester Info */}
              <div className="space-y-4 p-4 rounded-lg bg-slate-50">
                <h3 className="font-medium text-sm text-slate-600">Ihre Kontaktdaten</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="requester_name">Name *</Label>
                    <Input
                      id="requester_name"
                      value={formData.requester_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_name: e.target.value }))}
                      placeholder="Max Mustermann"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="requester_email">E-Mail *</Label>
                    <Input
                      id="requester_email"
                      type="email"
                      value={formData.requester_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, requester_email: e.target.value }))}
                      placeholder="max.mustermann@firma.ch"
                      required
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="requester_department">Abteilung (optional)</Label>
                  <Input
                    id="requester_department"
                    value={formData.requester_department}
                    onChange={(e) => setFormData(prev => ({ ...prev, requester_department: e.target.value }))}
                    placeholder="z.B. Vertrieb, IT, Marketing"
                  />
                </div>
              </div>

              {/* CR Details */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="title">Titel des Change Requests *</Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Kurze, aussagekräftige Beschreibung"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="description">Beschreibung *</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Beschreiben Sie die gewünschte Änderung detailliert. Was soll geändert werden? Wie soll es funktionieren?"
                    rows={4}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="reason">Begründung *</Label>
                  <Textarea
                    id="reason"
                    value={formData.reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Warum ist diese Änderung notwendig? Welches Problem wird damit gelöst?"
                    rows={3}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="priority">Priorität aus Ihrer Sicht</Label>
                  <Select 
                    value={formData.priority} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, priority: v }))}
                  >
                    <SelectTrigger id="priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Niedrig - Kann warten</SelectItem>
                      <SelectItem value="medium">Mittel - Sollte zeitnah umgesetzt werden</SelectItem>
                      <SelectItem value="high">Hoch - Dringend erforderlich</SelectItem>
                      <SelectItem value="critical">Kritisch - Blockiert wichtige Arbeit</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Die endgültige Priorität wird vom Projektleiter festgelegt.
                  </p>
                </div>

                {/* File Attachments */}
                <div>
                  <Label>Anhänge (optional)</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                      onChange={(e) => {
                        const files = Array.from(e.target.files);
                        const maxSize = 10 * 1024 * 1024; // 10MB
                        const validFiles = files.filter(f => f.size <= maxSize);
                        if (validFiles.length < files.length) {
                          setError('Einige Dateien waren zu gross (max. 10MB pro Datei)');
                        }
                        setAttachments(prev => [...prev, ...validFiles]);
                        e.target.value = '';
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      className="w-full"
                    >
                      <Paperclip className="w-4 h-4 mr-2" />
                      Dateien hinzufügen
                    </Button>
                    <p className="text-xs text-muted-foreground mt-1">
                      Bilder, PDFs, Word- oder Excel-Dateien (max. 10MB pro Datei)
                    </p>
                  </div>
                  
                  {/* Attachment Preview */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg">
                          {file.type.startsWith('image/') ? (
                            <FileImage className="w-4 h-4 text-blue-500" />
                          ) : file.type.includes('pdf') ? (
                            <FileText className="w-4 h-4 text-red-500" />
                          ) : (
                            <File className="w-4 h-4 text-gray-500" />
                          )}
                          <span className="text-sm flex-1 truncate">{file.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {(file.size / 1024).toFixed(0)} KB
                          </span>
                          <button
                            type="button"
                            onClick={() => setAttachments(prev => prev.filter((_, i) => i !== index))}
                            className="p-1 hover:bg-slate-200 rounded"
                          >
                            <X className="w-4 h-4 text-gray-500" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Submit */}
              <div className="pt-4 border-t">
                <Button 
                  type="submit" 
                  className="w-full" 
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Wird eingereicht...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Change Request einreichen
                    </>
                  )}
                </Button>
                <p className="text-xs text-center text-muted-foreground mt-3">
                  Nach dem Einreichen erhalten Sie eine Referenznummer zur Nachverfolgung.
                </p>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground mt-6">
          Powered by TrackSuite • Change Management
        </p>
      </div>
    </div>
  );
};

export default PublicCRForm;
